import React, { forwardRef } from 'react';
import { formatMoneyForLocale, useI18n } from '@stitch-fix/i18n';
import {
  Box,
  Carousel,
  Grid,
  Modal,
  ModalFooter,
  Text,
  useScreenSize,
} from '@stitch-fix/mode-react';
import ProductCard from '@stitch-fix/mode-react/ProductCard';
import { shopPdpPath } from '../../utils/urlUtils';
import scrollIntoViewWithHeader from '../../utils/scrollIntoViewWithHeader';
import { useProductDetailsPageContext } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';

const InventoryFailureAlert = forwardRef((_props, ref) => {
  const { locale } = useI18n();
  const [state, dispatch] = useProductDetailsPageContext();

  const isSmallScreenSize = ['sm'].includes(useScreenSize());

  const handleShopSimilarStylesClick = () => {
    dispatch({ type: 'CLOSE_INVENTORY_FAILURE_ALERT' });
    scrollIntoViewWithHeader(ref.current);
  };

  const similarItems =
    state.recommendedCollections
      ?.find(collection => collection.treatment === 'similar_items')
      ?.shopProducts?.nodes?.slice(0, 3) || [];

  const formatSkuImage = item => {
    return {
      alt: `${item?.brand?.name} - ${item.name}`,
      baseUrl: item?.variantBySkuId?.image?.baseUrl,
      mediaPath: item?.variantBySkuId?.image?.mediaPath,
      sizes: {
        sm: 320,
      },
    };
  };

  const shopItemPath = ({ skuId, url }) => {
    const shopUrl = shopPdpPath(url, {
      source: state.source,
      referringSkuId: state.pdpSkuId,
      outfitId: state.outfitId,
      collection: state.collectionName,
      subSource: 'similar-items',
      recommendedSkuId: skuId,
      queryId: state.queryId,
    }).replace('https://www.stitchfix.com', '');

    if (state.gated) {
      return shopUrl?.replace('product', state.urlScope);
    }

    return shopUrl;
  };

  const renderProductCards = () => {
    const aspectRatio = isSmallScreenSize ? '1:1' : '4:5';

    return similarItems.map(item => (
      <Box
        key={`inventory-failure-similar-item-${item.id}`}
        style={{ textAlign: 'left' }}
      >
        <ProductCard
          sku={item?.variantBySkuId?.id}
          heroMedia={formatSkuImage(item)}
          heroStageAspectRatio={aspectRatio}
          href={shopItemPath({
            skuId: item?.variantBySkuId?.id,
            url: item?.variantBySkuId?.url || '',
          })}
          brand={item?.brand?.name}
          name={item?.name}
          price={formatMoneyForLocale(locale, item?.variantBySkuId?.price, {
            subunits: true,
          })}
        />
      </Box>
    ));
  };

  return (
    <Modal
      isOpen={state.showInventoryFailureModal}
      onClose={() => dispatch({ type: 'CLOSE_INVENTORY_FAILURE_ALERT' })}
      heading="Sorry, this item just sold out"
      footer={
        <ModalFooter
          primaryAction={{
            text: 'Shop similar styles',
            onClick: handleShopSimilarStylesClick,
          }}
        />
      }
      variant="inset-small"
      size="medium"
      hAlign="center"
    >
      <Text setting="body-small" spacingBottom mt={1}>
        This item is popular—luckily, we found similar items we think
        you&rsquo;ll like.
      </Text>
      {isSmallScreenSize ? (
        <Carousel ariaLabel="Similar items" slideWidth="220px">
          {renderProductCards()}
        </Carousel>
      ) : (
        <Grid columns={3}>{renderProductCards()}</Grid>
      )}
    </Modal>
  );
});

export default InventoryFailureAlert;
